import { useCallback, useState } from "react";
import { useAppConfig, useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { ApiError, api } from "@/services/api";
import type { CheckoutErrorResponse, CheckoutResponse } from "@/types/api";
import { buildCheckoutData } from "@/utils/booking";
import { logError } from "@/utils/logError";

interface UseCheckoutReturn {
  loading: boolean;
  submitCheckout: () => Promise<CheckoutResponse | Error | null>;
  prepareBooking: () => Promise<Error | null>;
  cancelPendingBooking: () => Promise<Error | null>;
}

export function useCheckout(): UseCheckoutReturn {
  const { aid } = useAppConfig();
  const { state, actions } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const [loading, setLoading] = useState(false);

  const submitCheckout = useCallback(async (): Promise<CheckoutResponse | Error | null> => {
    const checkoutData = buildCheckoutData(state, aid);
    if (!checkoutData) {
      return null;
    }

    setLoading(true);

    try {
      const response = await api.submitCheckout(campgroundData.urls.checkout, checkoutData);
      actions.setBookingId(response.booking_id);
      actions.setBookingNumber(response.booking_number);
      actions.setBookingCreatedAt(new Date().toISOString());
      actions.setBookingExpiresAt(response.internal_canceling_info?.expires_at ?? null);
      setLoading(false);
      return response;
    } catch (err) {
      let checkoutError: Error;

      if (err instanceof ApiError) {
        const apiError = err as ApiError<CheckoutErrorResponse>;
        checkoutError = new Error(apiError.data?.non_field_errors?.[0]);
      } else {
        checkoutError = err instanceof Error ? err : new Error(String(err));
      }

      setLoading(false);
      return checkoutError;
    }
  }, [state, aid, actions, campgroundData.urls.checkout]);

  const prepareBooking = useCallback(async (): Promise<Error | null> => {
    if (!state.bookingId) {
      return null;
    }
    try {
      await api.prepareBooking(campgroundData.urls.prepare_booking, state.bookingId);
      return null;
    } catch (err) {
      logError(err, { context: "prepareBooking" });
      return err as Error;
    }
  }, [campgroundData.urls.prepare_booking, state.bookingId]);

  const cancelPendingBooking = useCallback(async (): Promise<Error | null> => {
    if (!state.bookingId) {
      return null;
    }
    try {
      await api.cancelPendingBooking(campgroundData.urls.cancel_pending_booking, state.bookingId);
      return null;
    } catch (err) {
      logError(err, { context: "cancelPendingBooking" });
      return err as Error;
    }
  }, [campgroundData.urls.cancel_pending_booking, state.bookingId]);

  return {
    loading,
    submitCheckout,
    prepareBooking,
    cancelPendingBooking,
  };
}
